import { auth } from "@/firebase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://elevated-backend-xyaskmbwha-uc.a.run.app/api";

export interface FeedbackUser {
  uid: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  suffix?: string;
  userType: "student" | "tutor";
  email: string;
}

export interface FeedbackRating {
  ratingId: string;
  sessionId: string;
  fromUserId: string;
  toUserId: string;
  overallScore: number;
  comment: string;
  createdAt: string;
  communicationScore?: number;
  punctualityScore?: number;
  knowledgeScore?: number;
  helpfulnessScore?: number;
}

export interface Feedback {
  id: string;
  reviewer: FeedbackUser;
  reviewee: FeedbackUser;
  rating: FeedbackRating;
  subject: string;
  date: string;
}

export interface FeedbackStats {
  total: number;
  excellent: number;
  good: number;
  needsImprovement: number;
  studentReviews: number;
  tutorReviews: number;
}

export interface CreateFeedbackData {
  session_id: string;
  from_user_id: string;
  to_user_id: string;
  overall_score: number;
  comment?: string;
  subject?: string;
  communication_score?: number;
  punctuality_score?: number;
  knowledge_score?: number;
  helpfulness_score?: number;
  // Student evaluation criteria
  understanding_score?: number;
  problemSolving_score?: number;
  retention_score?: number;
  improvement_score?: number;
  confidence_score?: number;
  engagement_score?: number;
  preparedness_score?: number;
  curiosity_score?: number;
  effort_score?: number;
  attitude_score?: number;
}

export interface SessionFeedbackData {
  session: {
    id: string;
    subject: string;
    startAt: any;
    endAt: any;
    mode: string;
    location?: string;
    sessionStatus: string;
    studentId: string;
    tutorId: string;
  };
  currentUser: {
    id: string;
    type: "student" | "tutor";
    firstName: string;
    lastName: string;
  };
  otherParticipant: {
    id: string;
    type: "student" | "tutor";
    firstName: string;
    lastName: string;
  };
  feedbackAlreadyGiven: boolean;
}

class FeedbackService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Fetch all feedbacks from the backend
   */
  async getAllFeedbacks(): Promise<Feedback[]> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/feedbacks/`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch feedbacks: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch feedbacks");
      }

      return result.data || [];
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
      throw error;
    }
  }

  /**
   * Fetch feedback statistics
   */
  async getFeedbackStats(): Promise<FeedbackStats> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/feedbacks/stats/`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch feedback stats: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch feedback stats");
      }

      return result.data;
    } catch (error) {
      console.error("Error fetching feedback stats:", error);
      throw error;
    }
  }

  /**
   * Archive a feedback
   */
  async archiveFeedback(feedbackId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/feedbacks/archive/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          feedback_id: feedbackId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to archive feedback: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to archive feedback");
      }
    } catch (error) {
      console.error("Error archiving feedback:", error);
      throw error;
    }
  }

  /**
   * Create a new feedback
   */
  async createFeedback(feedbackData: CreateFeedbackData): Promise<string> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/feedbacks/create/`, {
        method: "POST",
        headers,
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create feedback: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to create feedback");
      }

      // Create audit log for feedback submission
      try {
        const { createAuditLog } = await import('@/services/auditLogService');
        const { db, doc, getDoc } = await import('@/firebase');
        
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Get user names for the audit log
          let fromUserName = 'User';
          let fromUserEmail = '';
          let toUserName = 'User';
          let toUserEmail = '';
          let userType = 'student'; // default

          // Get current user data
          const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (currentUserDoc.exists()) {
            const userData = currentUserDoc.data();
            fromUserName = userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || 'User';
            fromUserEmail = userData.email || '';
            userType = userData.userType || 'student';
          }

          // Get recipient user data
          const toUserDoc = await getDoc(doc(db, 'users', feedbackData.to_user_id));
          if (toUserDoc.exists()) {
            const userData = toUserDoc.data();
            toUserName = userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || 'User';
            toUserEmail = userData.email || '';
          }

          await createAuditLog({
            action: userType === 'student' ? 'student_feedback' : 'tutor_feedback',
            type: 'system_event',
            details: `${userType === 'student' ? 'Student' : 'Tutor'} ${fromUserName} submitted feedback and rating (${feedbackData.overall_score.toFixed(1)}/5.0) for ${userType === 'student' ? 'tutor' : 'student'} ${toUserName}${feedbackData.comment ? ' with additional comments' : ''}`,
            performedById: currentUser.uid,
            performedByName: fromUserName,
            performedByEmail: fromUserEmail,
            affectedUserId: feedbackData.to_user_id,
            affectedUserName: toUserName,
            affectedUserEmail: toUserEmail,
            metadata: {
              sessionId: feedbackData.session_id,
              feedbackId: result.feedback_id,
              overallRating: feedbackData.overall_score,
              hasComments: !!feedbackData.comment,
              actionType: 'feedback_submission',
              evaluatorType: userType,
              evaluatedType: userType === 'student' ? 'tutor' : 'student',
              subject: feedbackData.subject
            }
          });
          console.log('Audit log created for feedback submission via API');
        }
      } catch (auditError) {
        console.error('Failed to create audit log for feedback submission:', auditError);
        // Don't fail the whole operation if audit log creation fails
      }

      return result.feedback_id;
    } catch (error) {
      console.error("Error creating feedback:", error);
      throw error;
    }
  }

  /**
   * Get session details for feedback form
   */
  async getSessionFeedbackData(sessionId: string): Promise<SessionFeedbackData> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/feedbacks/session/${sessionId}/`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch session feedback data: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch session feedback data");
      }

      return result.data;
    } catch (error) {
      console.error("Error fetching session feedback data:", error);
      throw error;
    }
  }
}

// Export a singleton instance
// Lazy singleton instance to avoid circular dependency issues
let feedbackServiceInstance: FeedbackService | null = null;

export function getFeedbackService(): FeedbackService {
  if (!feedbackServiceInstance) {
    feedbackServiceInstance = new FeedbackService();
  }
  return feedbackServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const feedbackService = new Proxy({} as FeedbackService, {
  get(target, prop) {
    return getFeedbackService()[prop as keyof FeedbackService];
  }
});

/**
 * Fetch all feedbacks - backward compatibility function
 */
export async function fetchAllFeedbacks(): Promise<Feedback[]> {
  return await feedbackService.getAllFeedbacks();
}

/**
 * Get feedback statistics - backward compatibility function
 */
export async function getFeedbackStats(): Promise<FeedbackStats> {
  return await feedbackService.getFeedbackStats();
}

/**
 * Archive a feedback - backward compatibility function
 */
export async function archiveFeedback(feedbackId: string): Promise<boolean> {
  try {
    await feedbackService.archiveFeedback(feedbackId);
    return true;
  } catch (error) {
    console.error("Error archiving feedback:", error);
    return false;
  }
}

/**
 * Format user display name
 */
export function formatUserName(user: FeedbackUser): string {
  const parts = [user.firstName];
  if (user.middleName) parts.push(user.middleName);
  parts.push(user.lastName);
  if (user.suffix) parts.push(user.suffix);
  return parts.join(" ");
}

/**
 * Get rating label based on score
 */
export function getRatingLabel(score: number): string {
  if (score >= 4.5) return "Excellent";
  if (score >= 3.5) return "Good";
  return "Needs Improvement";
}

/**
 * Get rating badge CSS class
 */
export function getRatingBadgeClass(score: number): string {
  if (score >= 4.5) return "bg-green-100 text-green-800";
  if (score >= 3.5) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
}

export default feedbackService;
